'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import { Car, ChevronLeft, ChevronRight } from 'lucide-react';
import { popularCategories } from '@/data/brands-popular';
import { useCartStore } from '@/stores/cart';
import { useLocale } from '@/hooks/useLocale';
import { translateCategory } from '@/i18n/categories';
import { t } from '@/i18n/t';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001/api';

interface VehicleCategory {
  id: number;
  name: string;
  slug: string;
  product_count: number;
}

export function CategoryNav() {
  const { locale } = useLocale();
  const selectedVehicle = useCartStore((state) => state.selectedVehicle);
  const ktype = selectedVehicle?.ktype;
  const [vehicleCategories, setVehicleCategories] = useState<VehicleCategory[]>([]);
  const [mounted, setMounted] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!ktype) {
      setVehicleCategories([]);
      return;
    }

    fetch(`${API_URL}/categories/vehicle-summary?ktype=${ktype}`)
      .then((res) => (res.ok ? res.json() : []))
      .then((data: VehicleCategory[]) => {
        setVehicleCategories(
          data
            .filter((c) => c.product_count > 0)
            .sort((a, b) => b.product_count - a.product_count)
            .slice(0, 12)
        );
      })
      .catch(() => setVehicleCategories([]));
  }, [ktype]);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      ro.disconnect();
    };
  }, [checkScroll, vehicleCategories, mounted]);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.6;
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  const showVehicleNav = mounted && ktype && vehicleCategories.length > 0;

  return (
    <nav className="hidden lg:block border-b bg-background">
      <div className="container mx-auto px-4 relative">
        {/* Left scroll arrow */}
        {canScrollLeft && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-8 h-8 rounded-full bg-background/95 border shadow-sm hover:bg-secondary transition-colors"
            aria-label={t(locale, 'nav.scrollLeft')}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        )}

        {/* Right scroll arrow */}
        {canScrollRight && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-8 h-8 rounded-full bg-background/95 border shadow-sm hover:bg-secondary transition-colors"
            aria-label={t(locale, 'nav.scrollRight')}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        )}

        {/* Fade edges when scrollable */}
        {canScrollLeft && (
          <div className="absolute left-8 top-0 bottom-0 w-6 bg-gradient-to-r from-background to-transparent z-[5] pointer-events-none" />
        )}
        {canScrollRight && (
          <div className="absolute right-8 top-0 bottom-0 w-6 bg-gradient-to-l from-background to-transparent z-[5] pointer-events-none" />
        )}

        <ul
          ref={scrollRef}
          className="flex items-center gap-1 py-1 text-sm overflow-x-auto scrollbar-hide"
        >
          {showVehicleNav ? (
            <>
              <li className="flex items-center gap-1 px-2 py-1.5 text-xs text-primary font-medium whitespace-nowrap">
                <Car className="h-3.5 w-3.5" />
                {selectedVehicle!.manufacturer} {selectedVehicle!.model}:
              </li>
              {vehicleCategories.map((cat) => (
                <li key={cat.id}>
                  <Link
                    href={`/katalog/${cat.slug}?ktype=${ktype}`}
                    className="inline-flex items-center px-3 py-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors whitespace-nowrap"
                  >
                    {translateCategory(cat.slug, cat.name, locale)}
                    <span className="ml-1.5 text-xs text-muted-foreground/60">
                      ({cat.product_count})
                    </span>
                  </Link>
                </li>
              ))}
            </>
          ) : (
            popularCategories.map((cat) => (
              <li key={cat.slug}>
                <Link
                  href={`/katalog/${cat.slug}`}
                  className="inline-flex items-center px-3 py-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors whitespace-nowrap"
                >
                  {translateCategory(cat.slug, cat.name, locale)}
                </Link>
              </li>
            ))
          )}
          <li>
            <Link
              href={ktype ? `/katalog?ktype=${ktype}` : '/katalog'}
              className="inline-flex items-center px-3 py-1.5 rounded-md text-primary font-medium hover:bg-primary/5 transition-colors whitespace-nowrap"
            >
              {t(locale, 'nav.allCategories')}
            </Link>
          </li>
        </ul>
      </div>
    </nav>
  );
}
